/**
 * features/finance/FeeSetup.tsx
 * ------------------------------------------------------------------
 * Fee configuration, in three parts:
 *
 *   1. Fee structures — per form band (Forms 1–3 / Form 4 / Form 5 /
 *                       A-Level), the lines a student in that band is
 *                       billed. Mandatory lines bill automatically;
 *                       optional lines are opt-in at billing time.
 *   2. (removed)      — see the note below.
 *   3. Settings       — due days, aging thresholds, late interest,
 *                       sibling discount.
 *
 * LKC bills tuition per form band; that's the granularity here. If the
 * school ever needs per-class or per-student variation, structures can
 * be keyed more finely without touching the engine.
 *
 * THE FEE CATALOG TAB WAS REMOVED. It offered a second place to define a
 * charge — a reusable named item that a structure line could then be
 * built from — and in practice the structures are where the school
 * actually works. Two ways to say what a charge is meant two places to
 * look when a figure was wrong. A structure line is now typed straight
 * in: "+ Add line", a label and an amount.
 *
 * WHAT THAT LEAVES BEHIND, stated plainly because it is not obvious from
 * this file: the `feeItems` collection still exists in Firestore and is
 * still read by the admissions AdmitWizard, which prefills a joining
 * invoice with the mandatory once-off and annual items (see
 * `oneTimeFees` there). Those items keep working exactly as before, but
 * there is no longer any screen that can add, edit or archive one. If a
 * joining fee needs changing, that is the reason it cannot be done here
 * — it needs either a new home for the catalog or an edit in Firestore.
 *
 * Writes go through financeWrite; each save is audited.
 * ------------------------------------------------------------------ */
import { useState, type ReactNode } from 'react';
import { Plus, Trash2, Save, SlidersHorizontal, Settings } from 'lucide-react';
import { AccentButton, GradientHero, SoftCard, glassFieldClass } from '@/features/finance/ui';
import { NumberField } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { SCHOOL, termLabel, type TermKey } from '@/lib/school.config';
import { useAuth } from '@/contexts/AuthContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { finance, newId } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import {
  FINANCE_COLLECTIONS, FINANCE_SETTINGS_ID, FORM_BANDS, formBandLabel,
  newStructureId, fmtPula, round2,
  type FeeStructure, type FeeStructureLine,
  type FinanceSettings,
} from '@/lib/financeCore';

const ALL_TERMS = Object.keys(SCHOOL.terms) as TermKey[];

type Actor = { id: string; email?: string | null };
type SaveFn = (fn: () => Promise<void>, msg: string) => Promise<void>;

export default function FeeSetup() {
  const data = useFinanceData();
  const { userData } = useAuth();
  const actor: Actor = { id: userData?.uid ?? 'unknown', email: userData?.email };
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [sub, setSub] = useState<'structures' | 'settings'>('structures');
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const save: SaveFn = async (fn, msg) => {
    setSc({ show: true, status: 'saving', message: 'Saving…' });
    try { await fn(); setSc({ show: true, status: 'success', message: msg }); }
    catch (e) { console.error(e); setSc({ show: true, status: 'error', message: 'Save failed — please retry.' }); }
  };

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading fees…" /></div>;
  }

  return (
    <div className="space-y-6">
      <GradientHero icon={SlidersHorizontal} title="Fees" subtitle="Per-form fee structures and finance settings" />
      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} />

      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
        {([
          { id: 'structures', label: 'Structures', icon: SlidersHorizontal },
          { id: 'settings', label: 'Settings', icon: Settings },
        ] as const).map((t) => {
          const Icon = t.icon; const on = t.id === sub;
          return (
            <button key={t.id} onClick={() => setSub(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                on ? 'border-[var(--accent-strong,#0f766e)] text-[var(--accent-link,#0d9488)] dark:text-[var(--accent-link-dark,#5eead4)]'
                   : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'}`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Structures by form band ─────────────────────────────── */}
      {sub === 'structures' && (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="text-[var(--accent-link,#0d9488)]" />
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Fee structures by form band</h3>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Academic year</span>
            <input value={year} onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} className={glassFieldClass('py-1.5 w-24 text-sm')} />
          </label>
        </div>

        {FORM_BANDS.map((band) => {
          const existing = data.feeStructures.find((s) => s.year === year && s.bandKey === band.key);
          return (
            <StructureCard
              key={`${year}_${band.key}`}
              year={year}
              bandKey={band.key}
              existing={existing}
              actor={actor}
              save={save}
            />
          );
        })}
      </div>
      )}


      {/* ── Settings ────────────────────────────────────────────── */}
      {sub === 'settings' && <SettingsPanel settings={data.settings} actor={actor} save={save} />}
    </div>
  );
}

/* ----------------------------- settings ---------------------------- */
function SettingsPanel({ settings, actor, save }: { settings: FinanceSettings; actor: Actor; save: SaveFn }) {
  const [dueDays, setDueDays] = useState(settings.dueDays);
  const [aging, setAging] = useState((settings.agingThresholds ?? [30, 60, 90]).join(', '));
  const [interest, setInterest] = useState(settings.lateInterestMonthlyPct ?? 2);
  const [prefix, setPrefix] = useState(settings.invoiceNoPrefix);
  const [appFee, setAppFee] = useState(settings.applicationFeeAmount ?? 0);
  const [regFee, setRegFee] = useState(settings.registrationFeeAmount ?? 0);
  const [splits, setSplits] = useState((settings.defaultInstalmentSplits ?? [50, 50]).join(', '));
  const [remBefore, setRemBefore] = useState((settings.remindBeforeDays ?? [7, 1]).join(', '));
  const [remAfter, setRemAfter] = useState((settings.remindAfterDays ?? [1, 7, 14]).join(', '));
  const [remOn, setRemOn] = useState(settings.remindersEnabled ?? true);
  const [digestOn, setDigestOn] = useState(settings.bursarDigestEnabled ?? true);

  const onSave = () => save(async () => {
    const thresholds = aging.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
    await finance.settings.set({
      ...settings,
      dueDays: Number(dueDays) || 0,
      agingThresholds: thresholds.length ? thresholds : [30, 60, 90],
      lateInterestMonthlyPct: Number(interest) || 0,
      invoiceNoPrefix: prefix.trim() || 'INV',
      applicationFeeAmount: Number(appFee) || 0,
      registrationFeeAmount: Number(regFee) || 0,
      defaultInstalmentSplits: (() => { const v = splits.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0); return v.length ? v : [50, 50]; })(),
      remindBeforeDays: remBefore.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0),
      remindAfterDays: remAfter.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0),
      remindersEnabled: remOn,
      bursarDigestEnabled: digestOn,
      updatedAt: new Date().toISOString(),
    });
    await recordFinanceAudit({ action: 'settings.update', actor, subjectId: FINANCE_SETTINGS_ID, after: { dueDays, interest } });
  }, 'Finance settings saved');

  const Field = ({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) => (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">{label}</span>
      {children}
      {hint && <span className="text-xs text-gray-400 mt-1 block">{hint}</span>}
    </label>
  );

  return (
    <div className="space-y-4">
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Payment terms</h3>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Payment due (days after invoice)" hint="Sets each invoice's due date — the payment deadline.">
            <NumberField value={dueDays} onValue={setDueDays} integer className={glassFieldClass('py-2 w-32')} />
          </Field>
          <Field label="Late interest (% per month)" hint="Charged on balances past due, used by Arrears.">
            <NumberField value={interest} onValue={setInterest} className={glassFieldClass('py-2 w-32')} />
          </Field>
          <Field label="Arrears aging buckets (days)" hint="Comma-separated, ascending — e.g. 30, 60, 90.">
            <input value={aging} onChange={(e) => setAging(e.target.value)} className={glassFieldClass('py-2')} />
          </Field>
          <Field label="Invoice number prefix">
            <input value={prefix} onChange={(e) => setPrefix(e.target.value)} className={glassFieldClass('py-2 w-32')} />
          </Field>
        </div>
      </SoftCard>

      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">New-student fees</h3>
          <p className="text-xs text-gray-400 mt-0.5">Collected up front at application/admission and confirmed at approval. Development, levies and term fees are billed automatically on admission and set in the fee structures.</p>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Application fee (BWP)" hint="Paid with the application; confirmed at approval.">
            <NumberField value={appFee} onValue={setAppFee} className={glassFieldClass('py-2 w-40')} />
          </Field>
          <Field label="Registration fee (BWP)" hint="Paid before admission; confirmed at approval.">
            <NumberField value={regFee} onValue={setRegFee} className={glassFieldClass('py-2 w-40')} />
          </Field>
        </div>
      </SoftCard>

      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Instalments &amp; reminders</h3>
          <p className="text-xs text-gray-400 mt-0.5">Default split for new term invoices, and when parents are reminded about each instalment deadline.</p>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Default instalment split (%)" hint="Comma-separated, should sum to 100 — e.g. 50, 50 for two parts; 100 for pay-in-full.">
            <input value={splits} onChange={(e) => setSplits(e.target.value)} className={glassFieldClass('py-2 w-40')} />
          </Field>
          <Field label="Remind days BEFORE due" hint="Comma-separated — e.g. 7, 1.">
            <input value={remBefore} onChange={(e) => setRemBefore(e.target.value)} className={glassFieldClass('py-2 w-40')} />
          </Field>
          <Field label="Remind days AFTER overdue" hint="Comma-separated — e.g. 1, 7, 14.">
            <input value={remAfter} onChange={(e) => setRemAfter(e.target.value)} className={glassFieldClass('py-2 w-40')} />
          </Field>
          <div className="flex flex-col gap-2 justify-center">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={remOn} onChange={(e) => setRemOn(e.target.checked)} /> Send parent reminders</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={digestOn} onChange={(e) => setDigestOn(e.target.checked)} /> Send bursar overdue digest</label>
          </div>
        </div>
      </SoftCard>

      {/* Discounts are NOT set here. Every concession the school grants is
          recorded against a named pupil — Fee Special Cases for staff
          children, bursaries and full-annual payment, Sibling Discounts for
          sibling concessions — so that each one has a pupil, a reason and an
          author behind it, and reaches the ledger by exactly one route. A
          school-wide percentage typed into a settings box has none of those
          things and quietly applies itself to everyone. */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Discounts</h3>
        </div>
        <div className="p-5 text-sm text-gray-600 dark:text-gray-300">
          Discounts are no longer configured here. Record them against a pupil in
          <span className="font-medium"> Fee Special Cases</span> (staff children, bursaries,
          full annual payment) or in <span className="font-medium">Sibling Discounts</span>.
          Each concession then carries a pupil, a reason and an author, and posts once to
          Discounts &amp; Bursaries Granted.
        </div>
        <div className="px-5 pb-5 flex justify-end">
          <AccentButton icon={Save} onClick={onSave}>Save settings</AccentButton>
        </div>
      </SoftCard>
    </div>
  );
}


/** Shared field layout for a discount rule (edit + new). */
/* --------------------------- structures --------------------------- */
function StructureCard({
  year, bandKey, existing, actor, save,
}: {
  year: string; bandKey: string; existing?: FeeStructure; actor: Actor; save: SaveFn;
}) {
  const [lines, setLines] = useState<(FeeStructureLine & { _id: string })[]>(
    (existing?.lines ?? []).map((l) => ({ ...l, _id: newId(FINANCE_COLLECTIONS.feeStructures) })),
  );

  /* WHICH TERMS THIS BAND IS BILLED IN.
     The engine has always honoured this (financeCore: a band whose `terms`
     excludes the current term is skipped and marked notBilledThisTerm), and
     the field's own comment has always said exam-year bands bill in Term 1 and
     Term 2 only. There was simply no way to set it: this screen was the one
     writer of a fee structure and it never sent the field, so every band
     billed in all three terms regardless. The control below is that gap
     closed. */
  const [terms, setTerms] = useState<TermKey[]>(
    existing?.terms && existing.terms.length > 0 ? existing.terms : ALL_TERMS,
  );

  const perTerm = round2(lines.filter((l) => l.mandatory).reduce((a, l) => a + (Number(l.amount) || 0), 0));
  const billedTerms = terms.length || ALL_TERMS.length;
  const annual = round2(perTerm * billedTerms);
  const partial = terms.length !== ALL_TERMS.length;
  const configured = !!existing;

  /* Never allow zero terms — a band billed in no term is silently unbillable,
     and the screen would give no hint why nobody got an invoice. */
  const toggleTerm = (t: TermKey) => setTerms((cur) => {
    const next = cur.includes(t) ? cur.filter((x) => x !== t) : ALL_TERMS.filter((a) => cur.includes(a) || a === t);
    return next.length ? next : cur;
  });

  /* One way to add a line: type it. The catalog dropdown that used to sit
     here offered a second, and a line's figure could then disagree with the
     catalog's without either being obviously wrong. */
  const addLine = () =>
    setLines((ls) => [
      ...ls,
      { _id: newId(FINANCE_COLLECTIONS.feeStructures), label: '', category: 'other', amount: 0, mandatory: true },
    ]);

  const patch = (id: string, p: Partial<FeeStructureLine>) => setLines((ls) => ls.map((l) => (l._id === id ? { ...l, ...p } : l)));
  const remove = (id: string) => setLines((ls) => ls.filter((l) => l._id !== id));

  const onSave = () => save(async () => {
    const id = newStructureId(year, bandKey);
    const clean: FeeStructureLine[] = lines.map((l) => ({
      feeItemId: l.feeItemId,
      label: l.label.trim() || 'Charge',
      category: l.category,
      amount: Number(l.amount) || 0,
      mandatory: l.mandatory,
    }));
    /* Store `terms` ONLY when it is a subset. All three = the default, written
       as undefined so the engine bills every term.

       This write is deliberately a full overwrite, not a merge. Sending
       `terms: undefined` (which the firebase wrapper strips) removes the field
       outright, which is exactly what switching a band back to all-three terms
       must do — a merge would leave the old two-term restriction in place and
       the band would quietly keep skipping Term 3.

       `createdAt` is passed through so it keeps meaning "created". financeSet
       falls back to now() when the caller omits it, so every save was
       relabelling this structure as newly created. */
    await finance.feeStructures.set(id, {
      year,
      bandKey,
      label: formBandLabel(bandKey),
      lines: clean,
      terms: terms.length === ALL_TERMS.length ? undefined : terms,
      active: true,
      createdAt: existing?.createdAt,
    });
    await recordFinanceAudit({
      action: 'feeStructure.save', actor, subjectId: id,
      after: { lines: clean.length, perTerm, billedTerms, terms: terms.join(',') },
    });
  }, `Saved ${formBandLabel(bandKey)} (${year})`);

  return (
    <SoftCard pad="p-0" hover={false}>
      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{formBandLabel(bandKey)}</h4>
          {!configured && <span className="text-[11px] text-amber-600 dark:text-amber-400">not set yet — pupils in this band cannot be billed</span>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums leading-none">{fmtPula(perTerm)}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">
            per term · <span className="font-medium text-gray-500 dark:text-gray-300">{fmtPula(annual)}</span>/yr
            {partial ? ` (${billedTerms} terms)` : ''}
          </div>
        </div>
      </div>
      <div className="p-5 space-y-2">
        {lines.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No lines yet — add tuition and any extras below.</p>}
        {lines.map((l) => (
          <div key={l._id} className="grid grid-cols-[1fr_7rem_auto_auto] gap-2 items-center">
            <input value={l.label} onChange={(e) => patch(l._id, { label: e.target.value })} placeholder="Charge label" className={glassFieldClass('py-1.5 text-sm')} />
            <NumberField value={l.amount} onValue={(n) => patch(l._id, { amount: n })} placeholder="0.00" className={glassFieldClass('py-1.5 text-sm text-right')} />
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              <input type="checkbox" checked={l.mandatory} onChange={(e) => patch(l._id, { mandatory: e.target.checked })} className="rounded" /> Mandatory
            </label>
            <button onClick={() => remove(l._id)} className="p-1.5 text-gray-400 hover:text-red-500" title="Remove line"><Trash2 size={15} /></button>
          </div>
        ))}

        {/* Billed in — all three by default; exam-year bands are set to Term 1 & 2. */}
        <div className="flex items-center gap-2 pt-2">
          <span className="text-[11px] uppercase tracking-wide text-gray-400">Billed in</span>
          <div className="flex gap-1">
            {ALL_TERMS.map((t) => {
              const on = terms.includes(t);
              return (
                <button key={t} type="button" onClick={() => toggleTerm(t)}
                  aria-pressed={on}
                  title={on ? `Billed in ${termLabel(t)} — click to exclude` : `Not billed in ${termLabel(t)} — click to include`}
                  className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-colors ${
                    on ? 'bg-primary text-white border-primary'
                       : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-600'}`}>
                  {termLabel(t)}
                </button>
              );
            })}
          </div>
          {partial && (
            <span className="text-[11px] text-amber-600 dark:text-amber-400">
              not billed in {ALL_TERMS.filter((t) => !terms.includes(t)).map(termLabel).join(', ')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button type="button" onClick={addLine}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-gray-200 dark:border-gray-600 text-sm font-medium hover:border-gray-300 dark:hover:border-gray-500 transition-colors">
            <Plus size={14} /> Add line
          </button>
          <div className="flex-1" />
          <AccentButton icon={Save} onClick={onSave}>Save structure</AccentButton>
        </div>
      </div>
    </SoftCard>
  );
}
